"use client"; // BlocksRenderer works on client only

import type { BlocksContent } from "@strapi/blocks-react-renderer";
import type { ReactNode } from "react";

import { BlocksRenderer } from "@strapi/blocks-react-renderer";
import clsx from "clsx";

import { TypoBlockquote } from "@/src/components/typography/TypoBlockquote";
import { TypoCode } from "@/src/components/typography/TypoCode";
import { TypoHeading } from "@/src/components/typography/TypoHeading";
import { TypoLink } from "@/src/components/typography/TypoLink";
import { TypoList } from "@/src/components/typography/TypoList";
import { TypoParagraph } from "@/src/components/typography/TypoParagraph";
import { ImageWithCaption } from "@/src/components/ui/ImageWithCaption";
import { logger } from "@/src/infrastructure/datadog/Datadog";
import { mapToImage } from "@/src/infrastructure/strapi/domain/StrapiApi";

export interface StrapiRichTextProps {
  readonly content: BlocksContent;
  readonly darkBackground?: boolean;
  readonly headingLevel?: 2 | 3 | 4 | 5 | 6 | false;
  readonly smallImages?: boolean;
}

export function StrapiRichText({
  content,
  darkBackground = false,
  headingLevel = false,
  smallImages = false,
}: StrapiRichTextProps): ReactNode {
  return (
    <BlocksRenderer
      blocks={{
        code: ({ children }) => (
          <TypoCode darkBackground={darkBackground}>{children}</TypoCode>
        ),

        heading: ({ children, level }) => {
          if (headingLevel && level >= headingLevel) {
            return <TypoHeading level={level}>{children}</TypoHeading>;
          }
          logger.warn(`<h${level}> is used in rich text`);
          return <div>{children}</div>;
        },

        image: ({ image: strapiImage }) => {
          const image = mapToImage({
            ...strapiImage,
            documentId: 0, // We don't know the image ID here
          });
          return (
            <ImageWithCaption
              className={smallImages ? clsx("m-auto w-full", "md:w-3/5") : ""}
              image={image}
            />
          );
        },

        link: ({ children, url }) => (
          <TypoLink darkBackground={darkBackground} href={url}>
            {children}
          </TypoLink>
        ),

        list: ({ children, format }) =>
          format === "ordered" ? (
            <TypoList ordered>{children}</TypoList>
          ) : (
            <TypoList>{children}</TypoList>
          ),

        paragraph: ({ children }) => <TypoParagraph>{children}</TypoParagraph>,

        quote: ({ children }) => (
          <TypoBlockquote darkBackground={darkBackground}>
            {children}
          </TypoBlockquote>
        ),
      }}
      content={content}
    />
  );
}
